import { useState } from 'react'

import { cn } from '@playbooks/ui/lib/utils'
import { Tooltip, TooltipTrigger } from '@playbooks/ui/components/tooltip'

import { useAppContext } from '../../contexts/AppContext'
import useMountEffect from '../../hooks/useMountEffect'
import { TooltipContentWithContainer } from './TooltipContentWithContainer'
import { useTranslation } from '../../contexts/localization/Localization'

/**
 * Disclaimer component that shows a tooltip with additional information by clicking on the info button.
 * @param disclaimer - An object containing the title and content of the disclaimer.
 */
export default function Disclaimer({
  disclaimer,
}: {
  disclaimer: { title: React.ReactNode; content: React.ReactNode }
}) {
  const { text } = useTranslation()
  const { rootRef } = useAppContext()
  const [isOpen, setIsOpen] = useState(false)

  // On Click outside of the tooltip, close it
  useMountEffect(() => {
    const handleClickOutside = () => setIsOpen(false)

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  })

  return (
    <Tooltip open={isOpen} delayDuration={500}>
      <div className="group/button text-sm text-foreground/50 mb-0.5 pl-0.5">
        <div className="flex items-center gap-1">
          {disclaimer.title}
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={text('aiDisclaimerButton')}
              disabled={isOpen}
              onClick={() => setIsOpen(!isOpen)}
              className={cn('group-hover/button:text-foreground', isOpen && 'pointer-events-none')}
            >
              ⓘ
            </button>
          </TooltipTrigger>
        </div>
        <TooltipContentWithContainer
          containerRef={rootRef}
          className="
          ring-1 ring-zinc-400/50 shadow-lg text-sm
          bg-background text-foreground/70 [&_svg]:fill-background [&_svg]:opacity-0
          flex gap-2 max-w-[220px]
          "
        >
          <p>ⓘ</p>
          <p>{disclaimer.content}</p>
        </TooltipContentWithContainer>
      </div>
    </Tooltip>
  )
}
